import React from 'react';
import { get, template } from 'lodash';
import { connect } from 'react-redux';
import { reduceLayerTwoData, reduceCreativesData, reduceNameBasedData, shouldShowTotalActivities } from 'helpers/data';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { convertToDisplayValue } from 'helpers/strings';

import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTacticPlacements } from 'services/tactics/duck';
import { fetchChannel, fetchChannelCallouts, fetchChannelCreatives, fetchChannelDynamicCreatives, fetchChannelTargetingTactics } from 'services/channel/duck';
import { fetchLayerTwoChannelSummary } from 'services/layerTwo/duck';
import { formatPercentage } from 'helpers/formatNumbers';
import { getActivitiesSettings, getAudioObjectiveSettings } from 'helpers/objectiveSettings';
import { setObjectiveShadow } from 'services/config/duck';
import { fetchActivities } from 'services/activities/duck';

// Components and Containers
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';
import { LayerTwoDashboardComponent } from 'containers/dashboards/DashboardBaseComponent';
import { Grid, Row } from 'react-bootstrap';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ExportPDF from 'containers/buttons/ExportPDF';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';

// Charts
import BarChart from 'components/molecules/charts/BarChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';
import StackedBarChart from 'components/molecules/charts/StackedBarChart';
import LineChart from 'components/molecules/charts/LineChart';
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';

// Tables
import ActivitiesTable from 'components/molecules/tables/ActivitiesTable';
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedDynamicCreativesReports from 'components/organisms/ProtectedDynamicCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';

function percentageToolTipFormatter(format) {
    return template(format, {
        interpolate: /{([\s\S]+?)}/g
    })({
        series: this.series,
        point: {
            ...this,
            y: formatPercentage(this.y)
        }
    });
}

function percentageFormatter() {
    return formatPercentage(this.value, { format: '0' });
}

class LayerTwoAudioContainer extends LayerTwoDashboardComponent {
    constructor(props) {
        super(props, 'audio');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entityMeta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchChannelCallouts(payload);
        this.props.fetchChannel(payload);
        this.props.fetchChannelCreatives(payload);
        this.props.fetchChannelDynamicCreatives(payload);
        this.props.fetchTacticPlacements(payload);
        this.props.fetchLayerTwoChannelSummary(payload);
        this.props.fetchChannelTargetingTactics(payload);
        this.props.fetchActivities(payload);
    }

    render() {
        const channel = this.getCurrentChannel();
        const stub = `l2-${channel}`;
        const {
            backfillBuckets,
            xAxisLabel,
            entityObjective,
            showTotalActivities,
            showTargetingTactics,
            showConversionRevenue,
            selectedTimeRange,
            entityObjectiveLabelCode
        } = this.props;
        const {
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            calloutTitle, isLabelCoded,
            showActivitiesData,
            primaryActivityField,
            primaryActivityFieldTerm,
            singularTerm,
            pluralTerm
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const storeData = this.props.layerTwo[channel];
        const hasLayerTwoSummaryData = get(storeData, 'summary.hasData', false);

        let childSummaryTableData = [];
        let childSeriesData = {};
        let rankedOrder = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(storeData.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
            childSeriesData = reducedData.childSeriesData;
            rankedOrder = storeData.summary.data.rankings.impressions.map(i => i.name);
        }

        const hasCalloutData = get(this.props, 'layerThreeChannel.callouts.params.hasData', false);
        const hasLayerThreePlacementsData = get(this.props, 'layerThreePlacements.params.hasData', null);
        const hasLayerThreeTargetingTacticsData = get(this.props, 'layerThreeChannel.targetingTactics.params.hasData', null);
        const hasLayerThreeChannelData = get(this.props, 'layerThreeChannel.hasData', false);
        const hasCreativesData = get(this.props, 'layerThreeChannel.creatives.params.hasData', null);
        const hasDynamicCreativesData = get(this.props, 'layerThreeChannel.dynamicCreatives.params.hasData', null);

        const callouts = get(this.props, 'layerThreeChannel.callouts.data', {});
        const calloutWindow = get(this.props, 'layerThreeChannel.callouts.params.timeRanges.previous.windowSize', null);
        const layerThreeChannelData = get(this.props, 'layerThreeChannel.data', {});
        const placements = get(this.props, 'layerThreePlacements', {});
        const targetingTactics = get(this.props, 'layerThreeChannel.targetingTactics', {});
        const creatives = get(this.props, 'layerThreeChannel.creatives', {});
        const userId = get(this.props, 'currentUser.id', null);

        let placementsData = null;
        const placementsParams = get(this.props, 'layerThreePlacements.params', {});
        if (hasLayerThreePlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }

        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'layerThreeChannelData.targetingTactics.params', {});
        if (hasLayerThreeTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }

        let creativesData = null;
        const creativesParams = get(this.props, 'layerThreeChannel.creatives.params', {});
        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives);
        }

        const isChannelAvailable = get(this.props, 'entityMeta.channelAvailability.audio', false);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');

        const dataSource = get(this.props, 'entityMeta.channelDataSources.audio', 'ttd');
        const {
            creativeColumns,
            placementsColumns,
            targetingTacticsColumns,
            placementsDefaultSorted,
            showCompletionRate,
            calloutSettings,
            summaryColumns
        } = getAudioObjectiveSettings(dataSource, entityObjective, eCPAActivitiesOrClicks, calloutTitle);

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        const activities = get(this.props, 'activities', {});
        const hasActivities = get(this.props, 'activities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Audio
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs     */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Layer Two Charts & Tables */}
                {hasLayerTwoSummaryData ? (
                    <StackedBarChart
                        title={`IMPRESSIONS BY ${aliasDescriptor}`}
                        exportName={`${stub}-impression-stacked-chart`}
                        y='impressions'
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        rankedOrder={rankedOrder}
                        data={childSeriesData}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}

                {hasLayerTwoSummaryData ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} SUMMARY`}
                        channel={this.getCurrentChannel()}
                        exportName={`${stub}-location-summary-table`}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        accessors={summaryColumns}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        cacheKey={`${stub}-location-summary-table-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}

                {/* Layer Three Charts */}
                {hasLayerThreeChannelData ? (
                    <BarChart
                        title="AUDIO IMPRESSIONS"
                        exportName={`${stub}-audio-impressions`}
                        data={layerThreeChannelData}
                        backfillBuckets={backfillBuckets}
                        y="impressions"
                        yAxisLabel="Impressions"
                        color={this.props.theme.orderedColors[0]}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelData} />
                )}

                {hasLayerThreeChannelData && showCompletionRate ? (
                    <LineChart
                        title="COMPLETION RATE"
                        exportName={`${stub}-completion-rate`}
                        data={{ [this.state.channel]: layerThreeChannelData }}
                        backfillBuckets={backfillBuckets}
                        yTitle="Completion Rate"
                        yOptions={{
                            tooltip: {
                                pointFormatter: percentageToolTipFormatter
                            }
                        }}
                        yAxisOptions={{
                            labels: {
                                formatter: percentageFormatter
                            }
                        }}
                        y="completion_rate"
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelData && showCompletionRate} />
                )}

                {/* Activities */}
                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title={`${pluralTerm.toUpperCase()} TRENDS`}
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={convertToDisplayValue(primaryActivityFieldTerm)}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {activitiesRenderFlag ? (
                    <ActivitiesTable
                        title={`${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        objectives={[entityObjective]}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {hasLayerThreePlacementsData ? (
                    <React.Fragment>
                        <GroupedBarChart
                            title="PLACEMENT PERFORMANCE"
                            exportName={`${stub}-placement-performance`}
                            data={placementsData.graph}
                            keys={placementsData.sortedKeys}
                            xAxisLabel={xAxisLabel}
                            backfillBuckets={backfillBuckets}
                            y="impressions"
                            animateDuration={2000}
                            xAxisType="datetime" />

                        <PlacementsTable
                            title="PLACEMENT ACTIVITY BREAKDOWN"
                            exportName={`${stub}-placement-activity-breakdown`}
                            data={placementsData.table}
                            params={placementsParams}
                            hasData={hasLayerThreePlacementsData}
                            defaultSorted={placementsDefaultSorted}
                            accessors={placementsColumns}
                            showTotalActivities={showTotalActivities}
                            showConversionRevenue={showConversionRevenue} />
                    </React.Fragment>
                ) : (<RenderingTile height='400px' show={hasLayerThreePlacementsData} />)}

                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasLayerThreeTargetingTacticsData}
                    accessors={targetingTacticsColumns}
                    // Same data format as Placements, so use those defaults
                    defaultSorted={placementsDefaultSorted}
                    showTotalActivities={showTotalActivities}
                    showTargetingTactics={showTargetingTactics}
                    showConversionRevenue={showConversionRevenue} />


                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    eCPAActivitiesOrClicksTerm={eCPAActivitiesOrClicksTerm}
                    showTotalActivities={showTotalActivities}
                    showConversionRevenue={showConversionRevenue}
                    accessors={creativeColumns}
                />

                <ProtectedDynamicCreativesReports
                    exportName={`${stub}-dynamic-creatives`}
                    hasDynamicCreativesData={hasDynamicCreativesData}
                    dynamicCreatives={get(this.props, 'layerThreeChannel.dynamicCreatives', {})}
                    dynamicProfiles={get(this.props, 'entityMeta.dynamicProfiles', {})}
                    entityObjective={entityObjective}
                />
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Audio
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                <NoChannelDataTile channel='Audio' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entityMeta: state.entities.meta,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showTargetingTactics: get(state, 'entities.meta.meta.showTargetingTactics', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    layerTwo: state.layerTwo,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    activities: state.activities.audio,

    // LayerThree Services
    layerThreeChannel: state.channel.audio,
    layerThreePlacements: state.tactics.placements,
    layerThreeActivities: state.activities.audio
});

export default connect(mapStateToProps, {
    fetchActivities,
    fetchEntityMeta,
    fetchChannel,
    fetchChannelCallouts,
    fetchChannelCreatives,
    fetchChannelDynamicCreatives,
    fetchChannelTargetingTactics,
    fetchTacticPlacements,
    fetchLayerTwoChannelSummary,
    setObjectiveShadow,
})(LayerTwoAudioContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerTwoAudioContainer.js